import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { HardHat, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'General Construction Labourers',
  'Framers & Carpenters',
  'Concrete Workers & Finishers',
  'Demolition Crews',
  'Roofing Workers',
  'Drywall Installers',
  'Painters & Finishing',
  'Site Clean-Up Crews',
  'Equipment Operators',
  'Scaffolding Workers',
];

const benefits = [
  'WCB-compliant workers ready for site',
  'Same-day to 48-hour placement for urgent needs',
  'Pre-screened for safety certifications',
  'Payroll and administration handled by WorkNation',
  'Temp-to-perm options available',
  'Replacement guarantee if worker doesn\u2019t meet standards',
];

const ConstructionPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Construction Staffing BC - WorkNation Labour Ltd | Construction Labour Hire Vancouver</title>
        <meta name="description" content="WorkNation Labour Ltd provides construction staffing across British Columbia. General labourers, framers, concrete workers, demolition crews, and skilled tradespeople. WCB compliant. 200+ workers placed. Contact us for fast placement." />
        <link rel="canonical" href="https://worknation.ca/industries/construction" />
        <meta property="og:title" content="Construction Staffing in British Columbia - WorkNation Labour Ltd" />
        <meta property="og:description" content="Reliable construction workers across BC. General labourers, framers, concrete, demolition & skilled trades. Fast placement, WCB compliant." />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-amber-50 via-white to-orange-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <HardHat className="text-amber-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-amber-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Construction Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd is a leading provider of construction staffing solutions across British Columbia. From Metro Vancouver to the Fraser Valley and beyond, we supply reliable, WCB-compliant workers for residential, commercial, and industrial construction projects. With <strong>200+ workers placed</strong>, we understand the fast-paced demands of the construction industry.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="construction-cta">
              Get Construction Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Construction Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Construction Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              British Columbia&apos;s construction industry is booming, with major residential and commercial developments across Metro Vancouver, Surrey, Burnaby, Richmond, and the Fraser Valley. WorkNation Labour Ltd supplies the workforce that keeps these projects on schedule and on budget.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Whether you need a single labourer for a day or a crew of 20 for a multi-month project, our team responds fast. Most construction staffing requests are fulfilled within 24-48 hours. Every worker is pre-screened, safety-trained, and covered by WCB before they step on your site.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our temp-to-perm program lets you trial workers on your construction site before committing to a permanent hire. This reduces hiring risk and helps you build a team of proven performers.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Construction Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default ConstructionPage;
